import React, { useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ReportDataPayload } from '../../shared/types/src';
import { useTheme } from '../../context/shared/ThemeContext'
import MentalHealthRadarChart from '../common/MentalHealthRadarChart'; 
// import logo from '../../assets/logo.png'; // Removed until logo is available

interface ReportPreviewProps {
    data: ReportDataPayload;
}

const ReportSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
    const { themeClasses } = useTheme();
    return (
        <div className="mb-6">
            <h3 className={`text-xl font-semibold border-b-2 border-indigo-500 pb-2 mb-3 ${themeClasses.primaryText}`}>{title}</h3>
            {children}
        </div>
    );
};

const ReportPreview: React.FC<ReportPreviewProps> = ({ data }) => {
    const { themeClasses } = useTheme();
    const reportRef = useRef<HTMLDivElement>(null);

    const handleDownloadPdf = () => {
        const input = reportRef.current;
        if (!input) return;

        html2canvas(input, { scale: 2, backgroundColor: '#111827' }).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            const ratio = canvasWidth / canvasHeight;
            const widthInPdf = pdfWidth;
            const heightInPdf = widthInPdf / ratio;

            let position = 0;
            if (heightInPdf > pdfHeight) {
                 // TODO: Handle multi-page PDFs
                 console.warn("Report is too long for a single PDF page. Multi-page support not yet implemented.");
            }
            
            pdf.addImage(imgData, 'PNG', 0, position, widthInPdf, heightInPdf);

            // Add Footer
            pdf.setFontSize(8);
            pdf.text("Mentra is a HIPAA-aligned AI tool for symbolic emotional support.", pdfWidth / 2, pdfHeight - 10, { align: 'center' });

            pdf.save(`Mentra-Report-${data.patientInfo.displayName.replace(' ', '_')}-${new Date().toISOString().split('T')[0]}.pdf`);
        });
    };
    
    const handlePrint = () => {
        window.print();
    }

    return (
        <div>
            <div className="flex justify-end space-x-2 mb-4">
                <button onClick={handleDownloadPdf} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">Download PDF</button>
                <button onClick={handlePrint} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Print Report</button>
            </div>

            <div ref={reportRef} className={`${themeClasses.cardBg} p-8 rounded-lg shadow-lg`}>
                <header className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className={`text-3xl font-bold ${themeClasses.primaryText}`}>Clinical Report</h1>
                        <p className={themeClasses.secondaryText}>{new Date().toLocaleDateString()}</p>
                    </div>
                    {/* <img src={logo} alt="Mentra Logo" className="h-12"/> */}
                </header>

                <ReportSection title="Patient Information">
                    <p className={themeClasses.primaryText}><strong>Name:</strong> {data.patientInfo.displayName}</p>
                    <p className={themeClasses.primaryText}><strong>UID:</strong> {data.patientInfo.uid}</p>
                    <p className={themeClasses.primaryText}><strong>Risk Status:</strong> {data.patientInfo.riskFlag ? `High Risk (${data.patientInfo.riskTriggerCount} triggers)` : 'Normal'}</p>
                </ReportSection>
                
                <ReportSection title="Alert Summary">
                     <p className={themeClasses.primaryText}><strong>Total Alerts:</strong> {data.alertSummary.totalAlerts}</p>
                     <p className={themeClasses.primaryText}><strong>Critical Alerts:</strong> {data.alertSummary.criticalAlerts}</p>
                </ReportSection>

                <ReportSection title="Symbolic Tag Summary (Top 5)">
                    <ul className={`${themeClasses.primaryText}`}>
                        {data.symbolicSummary.topTags.map(([tag, count]) => <li key={tag}>{tag} ({count} mentions)</li>)}
                    </ul>
                </ReportSection>

                <div style={{ pageBreakInside: 'avoid' }}>
                    <ReportSection title="Mental Health Radar">
                        <div className="bg-gray-800 p-4 rounded-lg">
                            <MentalHealthRadarChart />
                        </div>
                    </ReportSection>
                </div>


                <ReportSection title="Therapist Notes (Last 5)">
                     {data.therapistNotes.length > 0 ? (
                        <ul className={`list-disc pl-5 space-y-2 ${themeClasses.primaryText}`}>{data.therapistNotes.map((note, i) => <li key={i}>"{note}"</li>)}</ul>
                     ) : <p className={themeClasses.primaryText}>No notes in this period.</p>}
                </ReportSection>
                
                <ReportSection title="Patient Message Samples (Last 5)">
                     {data.messageSamples.patient.map((msg, i) => <p key={i} className={`italic p-2 bg-gray-900/50 rounded-md mb-2 ${themeClasses.primaryText}`}>"{msg}"</p>)}
                </ReportSection>

            </div>
        </div>
    );
};

export default ReportPreview; 